// src/audio/AudioProvider.tsx
import React, {createContext, useContext, useEffect, useMemo, useRef, useState} from "react";

type AudioCtx = {
    audio: HTMLAudioElement | null;
    playing: boolean;
    loading: boolean;
    error: string | null;
    volume: number;
    setVolume: (v: number) => void;
    play: () => Promise<void>;
    pause: () => void;
    setSrc: (src: string, mime?: string) => void;
};

const Ctx = createContext<AudioCtx | null>(null);

export function AudioProvider({ children }: { children: React.ReactNode }) {
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [playing, setPlaying] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const initialVol = useMemo(() => {
        const v = Number(localStorage.getItem("liveStream.volume"));
        return Number.isFinite(v) && v >= 0 && v <= 1 ? v : 0.8;
    }, []);
    const [volume, setVolume] = useState(initialVol);

    // Create the singleton once
    if (!audioRef.current) {
        audioRef.current = new Audio();
        audioRef.current.preload = "none";
    }
    const a = audioRef.current!;

    // Bind listeners once
    useEffect(() => {
        const onPlay = () => { setPlaying(true); setLoading(false); setError(null); };
        const onPause = () => setPlaying(false);
        const onWaiting = () => setLoading(true);
        const onCanPlay = () => setLoading(false);
        const onError = () => { setLoading(false); setPlaying(false); setError("Kon nie die stroom speel nie."); };

        a.addEventListener("play", onPlay);
        a.addEventListener("pause", onPause);
        a.addEventListener("waiting", onWaiting);
        a.addEventListener("canplay", onCanPlay);
        a.addEventListener("error", onError);
        return () => {
            a.removeEventListener("play", onPlay);
            a.removeEventListener("pause", onPause);
            a.removeEventListener("waiting", onWaiting);
            a.removeEventListener("canplay", onCanPlay);
            a.removeEventListener("error", onError);
        };
    }, [a]);

    // Volume persistence
    useEffect(() => {
        a.volume = volume;
        localStorage.setItem("liveStream.volume", String(volume));
    }, [a, volume]);

    // Optional: Media Session integration
    useEffect(() => {
        if ("mediaSession" in navigator) {
            navigator.mediaSession.setActionHandler("play", () => a.play().catch(() => {}));
            navigator.mediaSession.setActionHandler("pause", () => a.pause());
        }
    }, [a]);

    const api = useMemo<AudioCtx>(() => ({
        audio: a,
        playing,
        loading,
        error,
        volume,
        setVolume,
        play: async () => { await a.play(); },
        pause: () => a.pause(),
        setSrc: (src: string) => {
            if (a.src !== src) {
                const wasPlaying = !a.paused;
                a.src = src; // for live streams you usually don’t need type on <audio> programmatically
                a.load();
                if (wasPlaying) a.play().catch(() => {});
            }
        },
    }), [a, playing, loading, error, volume]);

    return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useGlobalAudio() {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error("useGlobalAudio must be used within <AudioProvider>");
    return ctx;
}
